import type { IdFonte } from '../fontes'
import type { Produto } from '../produtos'
import { emMgM3 } from '../unidades'
import { faixaVentoGre } from '../vento'

/**
 * A pluma aplicada a um produto do catálogo: que limiares desenhar, quando
 * avisar que o gás é denso e qual distância o GRE manda proteger.
 */

export type IdLimite = 'aegl3' | 'aegl2' | 'aegl1' | 'idlh' | 'lii' | 'meioLii'

export interface LimiteDesenhavel {
  id: IdLimite
  rotulo: string
  /** O que significa estar dentro deste contorno. */
  significado: string
  mgM3: number
  valorOriginal: string
  fonte: IdFonte
}

/** Limiares de concentração do produto, do mais alto ao mais baixo, já em mg/m³. */
export function limitesDoProduto(produto: Produto): LimiteDesenhavel[] {
  const M = produto.massaMolarGMol?.valor
  if (M == null) return []
  const limites: LimiteDesenhavel[] = []
  const texto = (valor: number, unidade: string) => `${valor.toLocaleString('pt-BR')} ${unidade === 'mg/m3' ? 'mg/m³' : 'ppm'}`

  if (produto.liiPorcento) {
    const ppm = produto.liiPorcento.valor * 10_000
    limites.push({
      id: 'lii',
      rotulo: 'LII',
      significado: 'Mistura inflamável: qualquer ignição dentro deste contorno queima a nuvem (incêndio em nuvem).',
      mgM3: emMgM3(ppm, 'ppm', M),
      valorOriginal: `${produto.liiPorcento.valor.toLocaleString('pt-BR')}% (${texto(ppm, 'ppm')})`,
      fonte: produto.liiPorcento.fonte,
    })
    limites.push({
      id: 'meioLii',
      rotulo: '50% do LII',
      significado: 'Margem usual de segurança para inflamabilidade: a média esconde bolsões acima do LII.',
      mgM3: emMgM3(ppm / 2, 'ppm', M),
      valorOriginal: texto(ppm / 2, 'ppm'),
      fonte: produto.liiPorcento.fonte,
    })
  }
  if (produto.aegl60) {
    const niveis = [
      ['aegl3', 'AEGL-3', 'Risco à vida ou morte para a população geral em 60 min de exposição.'],
      ['aegl2', 'AEGL-2', 'Efeitos graves, irreversíveis ou que impedem a fuga em 60 min de exposição.'],
      ['aegl1', 'AEGL-1', 'Desconforto e irritação notáveis, reversíveis, em 60 min de exposição.'],
    ] as const
    for (const [id, rotulo, significado] of niveis) {
      const limite = produto.aegl60[id]
      limites.push({ id, rotulo, significado, mgM3: emMgM3(limite.valor, limite.unidade, M), valorOriginal: texto(limite.valor, limite.unidade), fonte: limite.fonte })
    }
  }
  if (produto.idlh) {
    limites.push({
      id: 'idlh',
      rotulo: 'IDLH',
      significado: 'Imediatamente perigoso à vida e à saúde: só com proteção respiratória autônoma.',
      mgM3: emMgM3(produto.idlh.valor, produto.idlh.unidade, M),
      valorOriginal: texto(produto.idlh.valor, produto.idlh.unidade),
      fonte: produto.idlh.fonte,
    })
  }
  return limites.sort((a, b) => b.mgM3 - a.mgM3)
}

/**
 * Limite que define a zona quente da pluma: o IDLH quando existe; para
 * inflamável sem IDLH útil, 10% do LII; senão o AEGL-3.
 */
export function limiteZonaQuente(produto: Produto): { mgM3: number; rotulo: string } | null {
  const limites = limitesDoProduto(produto)
  const idlh = limites.find((l) => l.id === 'idlh')
  if (idlh) return { mgM3: idlh.mgM3, rotulo: `IDLH (${idlh.valorOriginal})` }
  const lii = limites.find((l) => l.id === 'lii')
  if (lii) return { mgM3: lii.mgM3 / 10, rotulo: '10% do LII' }
  const aegl3 = limites.find((l) => l.id === 'aegl3')
  return aegl3 ? { mgM3: aegl3.mgM3, rotulo: `AEGL-3 (${aegl3.valorOriginal})` } : null
}

/**
 * Gás que, ao vazar, se comporta como mais pesado que o ar: vapor mais denso
 * que o ar, ou gás liquefeito que sai como névoa fria (a amônia, apesar de
 * leve, forma nuvem densa ao vazar líquida).
 */
export function comportaComoGasDenso(produto: Produto): boolean {
  return (produto.densidadeVaporRelativa?.valor ?? 0) > 1 || produto.estadoTransporte === 'gasLiquefeitoPressurizado'
}

/** Motivo de o produto não caber nesta oficina, ou `null` quando cabe. */
export function motivoSemPluma(produto: Produto): string | null {
  if (produto.estadoTransporte === 'liquido') {
    return produto.pressaoVaporKPa && produto.pressaoVaporKPa.valor < 1
      ? 'Praticamente não evapora: o risco é de contato e de névoa, não de pluma.'
      : 'Líquido: a nuvem depende da evaporação da poça, que este laboratório ainda não modela.'
  }
  if (limitesDoProduto(produto).length === 0) return 'Sem limiares de concentração no catálogo para desenhar contornos.'
  return null
}

export interface AcaoProtetoraGre {
  isolarM: number
  protegerM: number
  descricao: string
  fonte: IdFonte
}

/**
 * Distâncias do GRE para comparar com a pluma calculada. Tóxicos por inalação
 * da Tabela 3 usam a linha de caminhão-tanque pela faixa de vento; os demais
 * caem na Tabela 1 ou, se o produto não é TIH, na evacuação do guia.
 */
export function acaoProtetoraGre(
  produto: Produto,
  { noite, ventoMS, grandeVazamento }: { noite: boolean; ventoMS: number; grandeVazamento: boolean },
): AcaoProtetoraGre | null {
  const { gre } = produto
  if (grandeVazamento && gre.tabela3CaminhaoTanque) {
    const t3 = gre.tabela3CaminhaoTanque
    const faixa = faixaVentoGre(ventoMS)
    const tabela = noite ? t3.protegerNoite : t3.protegerDia
    const protegerM = faixa === 'baixo' ? tabela.ventoBaixoM : faixa === 'moderado' ? tabela.ventoModeradoM : tabela.ventoAltoM
    return {
      isolarM: t3.isolarM,
      protegerM,
      descricao: `Tabela 3, caminhão-tanque, ${noite ? 'noite' : 'dia'}, vento ${faixa}`,
      fonte: t3.fonte,
    }
  }
  if (!grandeVazamento && gre.tabela1Pequeno) {
    const t1 = gre.tabela1Pequeno
    return {
      isolarM: t1.isolarM,
      protegerM: noite ? t1.protegerNoiteM : t1.protegerDiaM,
      descricao: `Tabela 1, pequeno vazamento, ${noite ? 'noite' : 'dia'}`,
      fonte: t1.fonte,
    }
  }
  if (grandeVazamento && gre.evacuacaoGrandeVazamentoM) {
    return {
      isolarM: gre.isolamentoInicialM.valor,
      protegerM: gre.evacuacaoGrandeVazamentoM.valor,
      descricao: `Guia ${produto.guiaGre.valor}: grande vazamento, evacuação inicial a favor do vento`,
      fonte: gre.evacuacaoGrandeVazamentoM.fonte,
    }
  }
  return { isolarM: gre.isolamentoInicialM.valor, protegerM: 0, descricao: `Guia ${produto.guiaGre.valor}: isolamento inicial`, fonte: gre.isolamentoInicialM.fonte }
}
